import { HealthFeedState, IndicatorState, StatusSummary } from '@/types';
import { getIndicator } from '@/services/indicators';
import { ConnectingIcon, StatusIcon } from './StatusIcon';
import { RelativeTime } from './RelativeTime';

interface StatusBannerProps {
  summary: StatusSummary;
  /** How much we actually know. Drives the verdict, not the other way round. */
  feedState: HealthFeedState;
  /** Why the health feed could not be read, when it could not be. */
  feedError?: Error | null;
  /**
   * The feed read cleanly but contains no service reports at all.
   *
   * Indistinguishable, from here, between "nothing has failed" and "nothing is
   * reporting". The verdict is left alone — a silent system genuinely has no
   * recorded faults — but the reader is told what the verdict rests on.
   */
  noReports?: boolean;
}

/**
 * The verdict. Everything above the fold on a status page is this one sentence.
 *
 * Shape borrowed from status.openai.com: a tinted header strip carrying the
 * verdict, and a plain body row carrying the caveat. That second row is why the
 * pattern is worth copying here — this page has something genuinely important
 * to say about the LIMITS of its own knowledge, and Cloudflare's "we're not
 * currently aware of any issues" phrasing says it without either overclaiming
 * or alarming anyone.
 *
 * Three separate bugs on this page reported green while blind. The guard
 * against a fourth is structural: the banner branches on feedState BEFORE it
 * ever looks at `summary.overall`, so a verdict that was never computed cannot
 * be rendered as a good one.
 */
export function StatusBanner({ summary, feedState, feedError, noReports }: StatusBannerProps) {
  if (feedState === 'connecting') {
    return (
      <Shell
        surface="bg-slate-50"
        border="border-slate-200"
        accent="bg-slate-300"
        icon={<ConnectingIcon className="h-6 w-6" />}
        headline="Checking service health…"
        headlineClass="text-slate-700"
      >
        <p className="text-sm text-slate-600">
          Reading the public health feed. No verdict yet — this is not a report
          that everything is fine.
        </p>
      </Shell>
    );
  }

  if (feedState === 'unavailable') {
    const denied = !!feedError?.message?.toLowerCase().includes('permission');
    return (
      <Shell
        surface="bg-slate-100"
        border="border-slate-300"
        accent="bg-slate-400"
        icon={<StatusIcon state="unknown" className="h-6 w-6" />}
        headline="Status Unavailable"
        headlineClass="text-slate-700"
      >
        <p className="text-sm text-slate-600">
          We could not read the health feed, so we cannot tell you whether
          Someta is working.{' '}
          <span className="text-slate-500">
            This is not the same as everything being fine — services may be up,
            down, or anywhere in between.
          </span>
        </p>
        <p className="mt-2 text-xs text-slate-500">
          {denied
            ? 'The public status feed rejected the read (permission denied).'
            : feedError?.message ?? 'The status feed did not respond.'}
        </p>
      </Shell>
    );
  }

  const state: IndicatorState = summary.overall;
  const indicator = getIndicator(state);
  const counts = countByStatus(summary);

  return (
    <Shell
      surface={indicator.surface}
      border={indicator.border}
      accent={indicator.dot}
      icon={<StatusIcon state={state} className="h-6 w-6" />}
      headline={indicator.headline}
      headlineClass={indicator.text}
      meta={<RelativeTime date={summary.lastUpdated} prefix="Checked" />}
    >
      <div className="flex flex-wrap items-center gap-2">
        {counts.map(({ key, label, dot }) => (
          <span
            key={key}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-900/5"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden />
            {label}
          </span>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Based on errors reported by Someta's own services in the last hour.
      </p>
      {noReports && (
        <p className="mt-2 text-xs text-slate-500">
          <span className="font-medium text-slate-600">Note:</span> no service
          has reported to this page yet. That is consistent with everything
          working, and also with nothing reporting — this verdict rests on
          silence rather than on a measurement.
        </p>
      )}
    </Shell>
  );
}

function countByStatus(summary: StatusSummary) {
  const rows = (['outage', 'degraded', 'operational'] as const).map((status) => {
    const count = summary.services.filter((s) => s.status === status).length;
    return {
      key: status,
      count,
      label: `${count} ${getIndicator(status).label.toLowerCase()}`,
      dot: getIndicator(status).dot,
    };
  });
  // Only surface a problem bucket when it has something in it; "0 outage" is
  // noise that dilutes the counts that matter.
  return rows.filter((r) => r.count > 0 || r.key === 'operational');
}

interface ShellProps {
  surface: string;
  border: string;
  /** Accent rail colour — the state's solid dot colour. */
  accent: string;
  icon: React.ReactNode;
  headline: string;
  headlineClass: string;
  /** Small right-aligned metadata; fills the space beside a short headline. */
  meta?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * One surface, not two.
 *
 * The first cut split a tinted header strip from a white body, which read as
 * two boxes arguing rather than one verdict, and left a wide empty gutter to
 * the right of a three-word headline. A single tinted card with a solid accent
 * rail carries the state just as loudly, and the metadata that used to be
 * buried under the counts now occupies that gutter.
 */
function Shell({
  surface,
  border,
  accent,
  icon,
  headline,
  headlineClass,
  meta,
  children,
}: ShellProps) {
  return (
    <section className={`relative overflow-hidden rounded-xl border ${border} ${surface}`}>
      <span className={`absolute inset-y-0 left-0 w-1 ${accent}`} aria-hidden />
      <div className="py-4 pl-5 pr-4 sm:py-5 sm:pl-6 sm:pr-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 shrink-0">{icon}</span>
            <div className="min-w-0">
              <h1 className={`text-lg font-semibold leading-tight sm:text-xl ${headlineClass}`}>
                {headline}
              </h1>
              <div className="mt-2.5">{children}</div>
            </div>
          </div>
          {meta && (
            <p className="hidden shrink-0 pt-1 text-right text-xs text-slate-500 sm:block">
              {meta}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
